import { Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { Register } from "../../../services/ApiService";
import { useState } from "react";
import { useAuth, useStore } from "../../../hooks";
import { validateDateOfBirth, validateEmail } from "../../../helpers";
import { BiLock } from "react-icons/bi";
import { FiEye, FiEyeOff } from "react-icons/fi";
import PhonePrefixSelect, {
  COUNTRY_CODES,
} from "../../cart/_modals/PhonePrefixSelect";
import { Toast } from "../../Toast";

type FormFields = {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  phone: string;
  dob: string;
  type: string;
};

type ValidationErrors = Partial<Record<keyof FormFields, string>>;

type Props = {
  show: boolean;
  handleClose: () => void;
  handleSuccess: () => void;
  handleSwitch: (newMode: any) => void;
};

const minAge = import.meta.env.VITE_APP_MIN_AGE;

const RegisterModal: React.FC<Props> = ({
  show,
  handleClose,
  handleSuccess,
  handleSwitch,
}) => {
  const { storeId } = useStore();
  const { setToken, setCustomer } = useAuth();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validated, setValidated] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormFields>({
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    phone: "",
    dob: "",
    type: "register",
  });

  const [errors, setErrors] = useState<ValidationErrors>({});

  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;
    const prevValue = formData[name as keyof typeof formData];

    if (["firstname", "lastname"].includes(name))
      value = value.replace(/[^a-zA-Z\s]/g, "");
    else if (["phone"].includes(name)) value = value.replace(/[^0-9]/g, "");

    const meaningfulChange = value.trim() !== prevValue?.toString().trim();

    if (meaningfulChange) {
      setErrors((prev) => ({ ...prev, [name]: "" })); // Clear the error when user types
    }

    setFormData((prev) => ({ ...prev, [name]: value.trim() }));
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.firstname) {
      newErrors.firstname = "Please enter your first name";
    }

    if (!formData.email) {
      newErrors.email = "Please enter your email address";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Invalid email address";
    }

    if (!formData.password) {
      newErrors.password = "Please enter your password";
    } else if (formData.password.length < 6) {
      newErrors.password = "Invalid password";
    }

    if (!formData.phone) {
      newErrors.phone = "Please enter your phone number";
    } else if (formData.phone.length != 10) {
      newErrors.phone = "Invalid phone number";
    }

    const dobError = validateDateOfBirth(formData.dob);
    if (dobError) {
      newErrors.dob = dobError;
    }

    setErrors(newErrors);
    setValidated(true);

    // Return true if there are no errors
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!validateForm()) return false;

    setIsLoading(true);

    try {
      const { status, message, data } = await Register({
        ...formData,
        store_id: storeId,
      });
      if (status) {
        setToken(data.token);
        setCustomer(data.customer_data);
        handleSuccess();
      } else {
        Toast.error(message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        className="login modal-mobile-bottom auth-modal modal-width"
        show={show}
        onHide={handleClose}
        fullscreen="sm-down"
        centered
      >
        <Form
          className="login_main"
          method="POST"
          validated={validated}
          onSubmit={handleSubmit}
        >
          <Modal.Header
            closeButton
            className="login-modal-header border-0 sticky-modal-header"
          >
            <div className="w-100 text-center">
              <Modal.Title className="h4">Create Account</Modal.Title>

              <p className="login-modal-subtitle mb-0">
                Register to continue your order
              </p>
            </div>
          </Modal.Header>

          <Modal.Body className="login-modal-body pt-0">
            <Row>
              <Col xs={6}>
                <Form.Group className="auth-group">
                  <Form.Label className="login-label">
                    First Name <span className="text-danger">*</span>
                  </Form.Label>

                  <div className="auth-input-wrap">
                    <Form.Control
                      type="text"
                      placeholder="First name"
                      name="firstname"
                      value={formData.firstname}
                      onChange={handleChange}
                      isInvalid={!!errors.firstname}
                      required
                    />
                  </div>

                  {errors.firstname && (
                    <Form.Control.Feedback className="d-block" type="invalid">
                      {errors.firstname}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col xs={6}>
                <Form.Group className="auth-group">
                  <Form.Label className="login-label">Last Name</Form.Label>

                  <div className="auth-input-wrap">
                    <Form.Control
                      type="text"
                      placeholder="Last name"
                      name="lastname"
                      value={formData.lastname}
                      onChange={handleChange}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="auth-group">
              <Form.Label className="login-label">
                Email Address <span className="text-danger">*</span>
              </Form.Label>

              <div className="auth-input-wrap">
                <Form.Control
                  type="email"
                  placeholder="Enter your email address"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                  required
                />
              </div>

              {errors.email && (
                <Form.Control.Feedback className="d-block" type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="auth-group position-relative">
              <Form.Label className="login-label">
                Password <span className="text-danger">*</span>
              </Form.Label>

              <div className="auth-input-wrap position-relative">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  isInvalid={!!errors.password}
                  required
                />

                <span
                  className="auth-password-toggle login-input-icon"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </span>
              </div>

              {errors.password && (
                <Form.Control.Feedback className="d-block" type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {/* <Form.Group className="auth-group">
        <Form.Label className="login-label">
          Phone Number <span className="text-danger">*</span>
        </Form.Label>

        <div className="auth-input-wrap">
          <Form.Control
            type="tel"
            placeholder="Enter your phone number"
            name="phone"
            maxLength={10}
            value={formData.phone}
            onChange={handleChange}
            isInvalid={!!errors.phone}
            required
          />
        </div>

        {errors.phone && (
          <Form.Control.Feedback type="invalid">
            {errors.phone}
          </Form.Control.Feedback>
        )}
      </Form.Group> */}

            <Form.Group className="auth-group">
              <Form.Label className="login-label">
                Phone Number <span className="text-danger">*</span>
              </Form.Label>

              <div className="phone_field_wrapper">
                {/* PREFIX */}
                <PhonePrefixSelect value={COUNTRY_CODES[0]} />

                {/* INPUT */}
                <Form.Control
                  type="tel"
                  placeholder="(201) 555-0123"
                  name="phone"
                  maxLength={10}
                  value={formData.phone}
                  onChange={handleChange}
                  isInvalid={!!errors.phone}
                  required
                  className="login-input phone-control"
                />

                {/* HELPER TEXT */}
                <div className="input-helper-text">
                  We’ll use this to keep you updated about your order.
                </div>
              </div>

              {errors.phone && (
                <Form.Control.Feedback className="d-block" type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="auth-group mb-4">
              <Form.Label className="login-label">
                Birthday{" "}
                <small className="text-muted">
                  (Receive special birthday offers)
                </small>
              </Form.Label>

              <div className="auth-input-wrap">
                <Form.Control
                  type="date"
                  name="dob"
                  value={formData.dob}
                  max={
                    new Date(
                      new Date().setFullYear(new Date().getFullYear() - minAge),
                    )
                      .toISOString()
                      .split("T")[0]
                  }
                  onChange={handleChange}
                  isInvalid={!!errors.dob}
                />
              </div>

              {errors.dob && (
                <Form.Control.Feedback className="d-block" type="invalid">
                  {errors.dob}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <button
              type="submit"
              className="login_btn d-flex align-items-center justify-content-center gap-2"
              disabled={isLoading}
            >
              {isLoading && (
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              )}
              Register
            </button>

            <div className="modal-bottom-text">
              Already have an account?
              <span
                className="cursor-pointer"
                onClick={() => handleSwitch("login")}
              >
                Login now
              </span>
            </div>

            <div className="auth-security-note">
              <BiLock size={18} />
              Your information is secure and encrypted
            </div>
          </Modal.Body>
        </Form>
      </Modal>
    </>
  );
};

export default RegisterModal;
